import { 
  collection, 
  query, 
  orderBy, 
  getDocs, 
  doc, 
  updateDoc, 
  deleteDoc,
  onSnapshot,
  where,
  limit,
  addDoc,
  getDoc,
  setDoc,
  serverTimestamp,
  increment,
  Timestamp,
  runTransaction
} from 'firebase/firestore';
import { db } from './firebaseConfig';

/**
 * Invoices Service - Handle all Firestore operations for invoices
 */

// Helper functions for date handling
const createTimestampFilter = (date, type) => {
  if (!date || !(date instanceof Date) || isNaN(date)) {
    console.warn(`Invalid ${type} date:`, date);
    return null;
  }
  
  try {
    return Timestamp.fromDate(date);
  } catch (error) {
    console.error(`Error converting ${type} date to timestamp:`, error);
    return null;
  }
};

const startOfDay = (date) => {
  if (!date || !(date instanceof Date) || isNaN(date)) {
    return null;
  }
  const newDate = new Date(date);
  newDate.setHours(0, 0, 0, 0);
  return newDate;
};

const endOfDay = (date) => {
  if (!date || !(date instanceof Date) || isNaN(date)) {
    return null;
  }
  const newDate = new Date(date);
  newDate.setHours(23, 59, 59, 999);
  return newDate;
};

// Collection references
const INVOICES_COLLECTION = 'invoices';
const INVOICE_COUNTERS_COLLECTION = 'invoiceCounters';

/**
 * Check if an invoice number already exists
 * @param {string} invoiceNumber - Invoice number to check
 * @returns {Promise<boolean>} True if exists, false otherwise
 */
const checkInvoiceNumberExists = async (invoiceNumber) => {
  try {
    const q = query(
      collection(db, INVOICES_COLLECTION),
      where('invoiceNumber', '==', invoiceNumber),
      limit(1)
    );
    const snapshot = await getDocs(q);
    return !snapshot.empty;
  } catch (error) {
    console.error('Error checking invoice number existence:', error);
    return false;
  }
};

/**
 * Get the highest existing invoice number for a given year
 * @param {number} year - Year to check
 * @returns {Promise<number>} Highest counter number found
 */
const getHighestInvoiceNumber = async (year) => {
  try {
    // Query for invoices in the specified year
    const q = query(
      collection(db, INVOICES_COLLECTION),
      where('invoiceNumber', '>=', `BSIN-01${year}`),
      where('invoiceNumber', '<=', `BSIN-12${year}`),
      orderBy('invoiceNumber', 'desc'),
      limit(50) // Get more results to find the highest counter
    );
    
    const snapshot = await getDocs(q);
    if (snapshot.empty) return 0;
    
    let highestCounter = 0;
    
    snapshot.forEach((doc) => {
      const invoiceNumber = doc.data().invoiceNumber;
      
      // Extract counter from BSIN-MMYYYY-XXX format
      const parts = invoiceNumber.split('-');
      if (parts.length === 3) {
        const counter = parseInt(parts[2], 10);
        if (!isNaN(counter) && counter > highestCounter) {
          highestCounter = counter;
        }
      }
    });
    
    return highestCounter;
  } catch (error) {
    console.error('Error getting highest invoice number:', error);
    return 0;
  }
};

/**
 * Check if invoice number already exists (exported for CSV import validation)
 * @param {string} invoiceNumber - Invoice number to check
 * @returns {Promise<boolean>} True if exists, false otherwise
 */
export const validateInvoiceNumberExists = checkInvoiceNumberExists;

/**
 * Generate next invoice number with duplicate prevention
 * @param {boolean} skipCounter - If true, skip counter generation (for CSV imports)
 * @returns {Promise<string|null>} Invoice number in format BSIN-MMYYYY-XXX, or null if skipCounter is true
 */
export const generateInvoiceNumber = async (skipCounter = false) => {
  if (skipCounter) {
    return null; // Caller will provide number for CSV import
  }
  
  const maxRetries = 5;
  let attempt = 0;
  
  while (attempt < maxRetries) {
    try {
      const now = new Date();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const year = now.getFullYear();
      const dateStr = `${month}${year}`;
      
      // Use transaction to ensure atomic counter increment
      const invoiceNumber = await runTransaction(db, async (transaction) => {
        const counterRef = doc(db, INVOICE_COUNTERS_COLLECTION, year.toString());
        const counterDoc = await transaction.get(counterRef);
        
        let nextNumber = 1;
        
        if (counterDoc.exists()) {
          nextNumber = counterDoc.data().count + 1;
          transaction.update(counterRef, {
            count: nextNumber,
            updatedAt: serverTimestamp()
          });
        } else {
          // Create new counter for this year
          transaction.set(counterRef, {
            count: 1,
            year: year.toString(),
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            manuallySet: false
          });
          nextNumber = 1;
        }
        
        return `BSIN-${dateStr}-${String(nextNumber).padStart(3, '0')}`;
      });
      
      // Double-check for duplicates
      const exists = await checkInvoiceNumberExists(invoiceNumber);
      if (!exists) {
        console.log(`✅ Generated unique invoice number: ${invoiceNumber}`);
        return invoiceNumber;
      }
      
      console.warn(`⚠️ Duplicate invoice number detected: ${invoiceNumber}, retrying...`);
      attempt++;
      
    } catch (error) {
      console.error(`Error generating invoice number (attempt ${attempt + 1}):`, error);
      attempt++;
      
      if (attempt >= maxRetries) {
        // Final fallback - use timestamp with year-based format
        const now = new Date();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const year = now.getFullYear();
        const timestamp = now.getTime().toString().slice(-6);
        const fallbackNumber = `BSIN-${month}${year}-${timestamp}`;
        
        console.error(`🚨 Using fallback invoice number: ${fallbackNumber}`);
        return fallbackNumber;
      }
      
      // Wait before retry
      await new Promise(resolve => setTimeout(resolve, 100 * attempt));
    }
  }
  
  throw new Error('Failed to generate unique invoice number after maximum retries');
};

/**
 * Create a new invoice
 * @param {Object} invoiceData - Invoice data
 * @returns {Promise<Object>} Created invoice with ID
 */
export const createInvoice = async (invoiceData) => {
  try {
    const invoiceNumber = await generateInvoiceNumber();

    const invoice = {
      ...invoiceData,
      invoiceNumber,
      status: 'draft',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      version: 1
    };

    const docRef = await addDoc(collection(db, INVOICES_COLLECTION), invoice);

    // Trigger schedule sync for new invoices with dates
    const hasDates = invoice.dates && Array.isArray(invoice.dates) && invoice.dates.length > 0;
    console.log('🔍 New invoice creation - checking for dates:', {
      hasDates,
      dates: invoice.dates,
      invoiceType: invoice.invoiceType,
      invoiceId: invoice.id,
      invoiceNumber: invoice.invoiceNumber
    });

    if (hasDates) {
      try {
        console.log('🔄 Triggering schedule sync for new invoice...');
        // Import scheduleService dynamically to avoid circular dependency
        const { syncSchedulesFromInvoices } = await import('./scheduleService');
        const syncResult = await syncSchedulesFromInvoices();
        console.log('✅ Schedule sync completed after new invoice creation:', syncResult);
      } catch (error) {
        console.error('⚠️ Failed to sync schedules after new invoice creation:', error);
        // Don't fail the invoice creation if schedule sync fails
      }
    } else {
      console.log('⏭️ No dates found in new invoice - skipping schedule sync');
    }

    return {
      id: docRef.id,
      ...invoice,
      invoiceNumber
    };
  } catch (error) {
    console.error('Error creating invoice:', error);
    throw error;
  }
};

/**
 * Update an existing invoice
 * @param {string} invoiceId - Invoice ID
 * @param {Object} updates - Updated fields
 * @returns {Promise<boolean>} Success status
 */
export const updateInvoice = async (invoiceId, updates) => {
  try {
    const invoiceRef = doc(db, INVOICES_COLLECTION, invoiceId);
    await updateDoc(invoiceRef, {
      ...updates,
      updatedAt: serverTimestamp(),
      version: increment(1)
    });

    // Trigger schedule sync if date fields were updated
    const legacyDateFields = ['leadTime', 'serviceDate', 'rentalStartDate', 'rentalEndDate'];
    const hasLegacyDateUpdates = legacyDateFields.some(field => updates.hasOwnProperty(field));

    // Also check for unified dates array updates
    const hasUnifiedDateUpdates = updates.hasOwnProperty('dates');

    const hasDateUpdates = hasLegacyDateUpdates || hasUnifiedDateUpdates;

    console.log('🔍 Date update check:', {
      hasLegacyDateUpdates,
      hasUnifiedDateUpdates,
      hasDateUpdates,
      updatedFields: Object.keys(updates)
    });

    if (hasDateUpdates) {
      try {
        console.log('🔄 Triggering schedule sync after invoice update...');
        // Import scheduleService dynamically to avoid circular dependency
        const { syncSchedulesFromInvoices } = await import('./scheduleService');
        const syncResult = await syncSchedulesFromInvoices();
        console.log('✅ Schedule sync completed after invoice update:', syncResult);
      } catch (error) {
        console.error('⚠️ Failed to sync schedules after invoice update:', error);
        // Don't fail the invoice update if schedule sync fails
      }
    } else {
      console.log('⏭️ No date updates detected - skipping schedule sync');
    }

    return true;
  } catch (error) {
    console.error('Error updating invoice:', error);
    return false;
  }
};

/**
 * Get all invoices with real-time updates
 * @param {Function} callback - Function to call when data updates
 * @param {Object} options - Query options (status, startDate, endDate, limit, orderBy)
 * @returns {Function} Unsubscribe function
 */
export const subscribeToInvoices = (callback, options = {}) => {
  try {
    let q = collection(db, INVOICES_COLLECTION);
    
    // Add filters
    if (options.status) {
      q = query(q, where('status', '==', options.status));
    }
    
    if (options.quotationId) {
      q = query(q, where('quotationId', '==', options.quotationId));
    }
    
    // Add date range filters with proper boundary handling
    // Only add date filters if at least one date is provided (not null)
    if (options.startDate) {
      const startTimestamp = createTimestampFilter(startOfDay(options.startDate), 'start');
      if (startTimestamp) {
        q = query(q, where('createdAt', '>=', startTimestamp));
      }
    }
    if (options.endDate) {
      const endTimestamp = createTimestampFilter(endOfDay(options.endDate), 'end');
      if (endTimestamp) {
        q = query(q, where('createdAt', '<=', endTimestamp));
      }
    }
    
    // Add ordering
    const orderField = options.orderBy || 'createdAt';
    const orderDirection = options.orderDirection || 'desc';
    q = query(q, orderBy(orderField, orderDirection));
    
    // Add limit
    if (options.limit) {
      q = query(q, limit(options.limit));
    }

    console.log('📊 InvoiceService: Setting up subscription with options:', {
      hasStartDate: !!options.startDate,
      hasEndDate: !!options.endDate,
      isAllTime: !options.startDate && !options.endDate,
      orderBy: orderField
    });

    return onSnapshot(q, (snapshot) => {
      const invoices = [];
      snapshot.forEach((doc) => {
        invoices.push({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate(),
          updatedAt: doc.data().updatedAt?.toDate()
        });
      });
      console.log(`✅ InvoiceService: Received ${invoices.length} invoices`);
      callback(invoices);
    }, (error) => {
      console.error('Error fetching invoices:', error);
      callback([]);
    });
  } catch (error) {
    console.error('Error setting up invoices subscription:', error);
    callback([]);
    return () => {};
  }
};

/**
 * Get invoices once (without real-time updates)
 * @param {Object} options - Query options (status, startDate, endDate, limit, orderBy)
 * @returns {Promise<Array>} Array of invoices
 */
export const getInvoices = async (options = {}) => {
  try {
    let q = collection(db, INVOICES_COLLECTION);
    
    if (options.status) {
      q = query(q, where('status', '==', options.status));
    }
    
    // Add date range filters with proper boundary handling
    if (options.startDate) {
      const startTimestamp = createTimestampFilter(startOfDay(options.startDate), 'start');
      if (startTimestamp) {
        q = query(q, where('createdAt', '>=', startTimestamp));
      }
    }
    if (options.endDate) {
      const endTimestamp = createTimestampFilter(endOfDay(options.endDate), 'end');
      if (endTimestamp) {
        q = query(q, where('createdAt', '<=', endTimestamp));
      }
    }
    
    const orderField = options.orderBy || 'createdAt';
    const orderDirection = options.orderDirection || 'desc';
    q = query(q, orderBy(orderField, orderDirection));
    
    if (options.limit) {
      q = query(q, limit(options.limit));
    }

    const snapshot = await getDocs(q);
    const invoices = [];
    
    snapshot.forEach((doc) => {
      invoices.push({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate()
      });
    });
    
    return invoices;
  } catch (error) {
    console.error('Error getting invoices:', error);
    return [];
  }
};

/**
 * Get a single invoice by ID
 * @param {string} invoiceId - Invoice ID
 * @returns {Promise<Object|null>} Invoice data or null
 */
export const getInvoiceById = async (invoiceId) => {
  try {
    const invoiceRef = doc(db, INVOICES_COLLECTION, invoiceId);
    const invoiceSnap = await getDoc(invoiceRef);
    
    if (invoiceSnap.exists()) {
      return {
        id: invoiceSnap.id,
        ...invoiceSnap.data(),
        createdAt: invoiceSnap.data().createdAt?.toDate(),
        updatedAt: invoiceSnap.data().updatedAt?.toDate()
      };
    }
    
    return null;
  } catch (error) {
    console.error('Error getting invoice:', error);
    return null;
  }
};

/**
 * Delete an invoice and its associated schedules
 * @param {string} invoiceId - Invoice ID
 * @returns {Promise<boolean>} Success status
 */
export const deleteInvoice = async (invoiceId) => {
  try {
    console.log(`🗑️ Starting invoice deletion process for: ${invoiceId}`);
    
    // First, delete associated schedules
    try {
      // Import scheduleService dynamically to avoid circular dependency
      const { deleteSchedulesByInvoiceId } = await import('./scheduleService');
      const scheduleResult = await deleteSchedulesByInvoiceId(invoiceId);
      
      if (scheduleResult.success) {
        console.log(`✅ Schedule cleanup completed: ${scheduleResult.message || `${scheduleResult.deletedCount} schedules deleted`}`);
      } else {
        console.warn(`⚠️ Schedule cleanup failed: ${scheduleResult.error}`);
        // Continue with invoice deletion even if schedule cleanup fails
      }
    } catch (scheduleError) {
      console.error('⚠️ Error during schedule cleanup (continuing with invoice deletion):', scheduleError);
      // Don't fail the invoice deletion if schedule cleanup fails
    }
    
    // Then delete the invoice
    await deleteDoc(doc(db, INVOICES_COLLECTION, invoiceId));
    console.log(`✅ Invoice ${invoiceId} deleted successfully`);
    
    return true;
  } catch (error) {
    console.error('Error deleting invoice:', error);
    return false;
  }
};

/**
 * Update invoice status
 * @param {string} invoiceId - Invoice ID
 * @param {string} newStatus - New status
 * @returns {Promise<boolean>} Success status
 */
export const updateInvoiceStatus = async (invoiceId, newStatus) => {
  return updateInvoice(invoiceId, { status: newStatus });
};

/**
 * Get invoice statistics
 * @param {Object} options - Options for date range filtering
 * @returns {Promise<Object>} Statistics object
 */
export const getInvoiceStats = async (options = {}) => {
  try {
    const invoices = await getInvoices(options);
    
    const stats = {
      total: invoices.length,
      draft: invoices.filter(i => i.status === 'draft').length,
      sent: invoices.filter(i => i.status === 'sent').length,
      paid: invoices.filter(i => i.status === 'paid').length,
      overdue: invoices.filter(i => i.status === 'overdue').length,
      cancelled: invoices.filter(i => i.status === 'cancelled').length,
      thisMonth: 0,
      thisWeek: 0,
      totalAmount: 0,
      paidAmount: 0,
      pendingAmount: 0
    };

    // Calculate this month and week stats only if no date range is specified
    if (!options.startDate && !options.endDate) {
      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const startOfWeek = new Date(now.setDate(now.getDate() - now.getDay()));
      
      stats.thisMonth = invoices.filter(i => 
        i.createdAt && i.createdAt >= startOfMonth
      ).length;
      
      stats.thisWeek = invoices.filter(i => 
        i.createdAt && i.createdAt >= startOfWeek
      ).length;
    }

    // Calculate amounts
    stats.totalAmount = invoices.reduce((sum, i) => sum + (i.grandTotal || 0), 0);
    stats.paidAmount = invoices.filter(i => i.status === 'paid').reduce((sum, i) => sum + (i.grandTotal || 0), 0);
    stats.pendingAmount = invoices.filter(i => ['sent', 'overdue'].includes(i.status)).reduce((sum, i) => sum + (i.grandTotal || 0), 0);

    return stats;
  } catch (error) {
    console.error('Error getting invoice statistics:', error);
    return {
      total: 0,
      draft: 0,
      sent: 0,
      paid: 0,
      overdue: 0,
      cancelled: 0,
      thisMonth: 0,
      thisWeek: 0,
      totalAmount: 0,
      paidAmount: 0,
      pendingAmount: 0
    };
  }
};

/**
 * Search invoices
 * @param {string} searchTerm - Search term
 * @returns {Promise<Array>} Filtered invoices
 */
export const searchInvoices = async (searchTerm) => {
  try {
    const invoices = await getInvoices();
    const searchLower = searchTerm.toLowerCase();
    
    return invoices.filter(invoice => 
      invoice.companyName?.toLowerCase().includes(searchLower) ||
      invoice.attn?.toLowerCase().includes(searchLower) ||
      invoice.email?.toLowerCase().includes(searchLower) ||
      invoice.invoiceNumber?.toLowerCase().includes(searchLower)
    );
  } catch (error) {
    console.error('Error searching invoices:', error);
    return [];
  }
};

/**
 * Get available rental invoices for damaged items reference
 * @returns {Promise<Array>} Array of rental/rental_extension invoices
 */
export const getAvailableRentalInvoices = async () => {
  try {
    const invoices = await getInvoices();
    
    // Filter to only rental and rental_extension invoices
    const rentalInvoices = invoices.filter(invoice => 
      invoice.invoiceType === INVOICE_TYPES.RENTAL || 
      invoice.invoiceType === INVOICE_TYPES.RENTAL_EXTENSION
    );
    
    // Sort by creation date (newest first)
    rentalInvoices.sort((a, b) => {
      const dateA = a.createdAt || new Date(0);
      const dateB = b.createdAt || new Date(0);
      return dateB.getTime() - dateA.getTime();
    });
    
    return rentalInvoices;
  } catch (error) {
    console.error('Error getting available rental invoices:', error);
    return [];
  }
};

// Export status constants
export const INVOICE_STATUSES = {
  DRAFT: 'draft',
  SENT: 'sent',
  PAID: 'paid',
  OVERDUE: 'overdue',
  CANCELLED: 'cancelled'
};

export const INVOICE_STATUS_LABELS = {
  [INVOICE_STATUSES.DRAFT]: 'Draft',
  [INVOICE_STATUSES.SENT]: 'Sent',
  [INVOICE_STATUSES.PAID]: 'Paid',
  [INVOICE_STATUSES.OVERDUE]: 'Overdue',
  [INVOICE_STATUSES.CANCELLED]: 'Cancelled'
};

export const INVOICE_STATUS_COLORS = {
  [INVOICE_STATUSES.DRAFT]: '#8c8c8c',     // Gray
  [INVOICE_STATUSES.SENT]: '#1890ff',     // Blue
  [INVOICE_STATUSES.PAID]: '#52c41a',     // Green
  [INVOICE_STATUSES.OVERDUE]: '#ff4d4f',  // Red
  [INVOICE_STATUSES.CANCELLED]: '#d9d9d9' // Light gray
};

// Export invoice types (same as quotation types)
export const INVOICE_TYPES = {
  PURCHASE: 'purchase',
  RENTAL: 'rental',
  RENTAL_EXTENSION: 'rental_extension',
  SCAFFOLDING_SERVICE: 'scaffolding_service',
  DAMAGED_ITEMS: 'damaged_items'
};

export const INVOICE_TYPE_LABELS = {
  [INVOICE_TYPES.PURCHASE]: 'Purchase',
  [INVOICE_TYPES.RENTAL]: 'Rental',
  [INVOICE_TYPES.RENTAL_EXTENSION]: 'Rental Extension',
  [INVOICE_TYPES.SCAFFOLDING_SERVICE]: 'Scaffolding Service',
  [INVOICE_TYPES.DAMAGED_ITEMS]: 'Damaged Items'
};

// Export payment terms
export const PAYMENT_TERMS = {
  DEPOSIT_50: '50% deposit, rest upon completion',
  FULL_IMMEDIATE: 'Payment due immediately',
  CUSTOM: 'custom'
};

/**
 * Counter Management Functions for Emergency Use
 */

/**
 * Get current invoice counter value for a year
 * @param {number} year - Year to get counter for
 * @returns {Promise<Object>} Counter information
 */
export const getInvoiceCounterValue = async (year) => {
  try {
    const counterRef = doc(db, INVOICE_COUNTERS_COLLECTION, year.toString());
    const counterDoc = await getDoc(counterRef);
    
    if (counterDoc.exists()) {
      return {
        count: counterDoc.data().count,
        year: counterDoc.data().year,
        createdAt: counterDoc.data().createdAt?.toDate(),
        updatedAt: counterDoc.data().updatedAt?.toDate(),
        manuallySet: counterDoc.data().manuallySet || false
      };
    }
    
    return {
      count: 0,
      year: year.toString(),
      createdAt: null,
      updatedAt: null,
      manuallySet: false
    };
  } catch (error) {
    console.error('Error getting invoice counter value:', error);
    return null;
  }
};

/**
 * Set invoice counter value manually (for emergency use)
 * @param {number} year - Year to set counter for
 * @param {number} newValue - New counter value
 * @param {string} reason - Reason for manual change
 * @returns {Promise<Object>} Result with success status and message
 */
export const setInvoiceCounterValue = async (year, newValue, reason = 'Manual adjustment') => {
  try {
    // Validate input
    if (!Number.isInteger(newValue) || newValue < 0) {
      return {
        success: false,
        message: 'Counter value must be a non-negative integer'
      };
    }
    
    // Get highest existing invoice number for this year to prevent duplicates
    const highestExisting = await getHighestInvoiceNumber(year);
    
    if (newValue <= highestExisting) {
      return {
        success: false,
        message: `Cannot set counter to ${newValue}. Highest existing invoice number is ${highestExisting}. Set to ${highestExisting + 1} or higher.`,
        suggestedValue: highestExisting + 1
      };
    }
    
    // Set the counter value
    const counterRef = doc(db, INVOICE_COUNTERS_COLLECTION, year.toString());
    await setDoc(counterRef, {
      count: newValue,
      year: year.toString(),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      manuallySet: true,
      manualSetReason: reason,
      manualSetTimestamp: serverTimestamp()
    }, { merge: true });
    
    console.log(`✅ Invoice counter for ${year} manually set to ${newValue}. Reason: ${reason}`);
    
    return {
      success: true,
      message: `Counter successfully set to ${newValue}`,
      newValue: newValue
    };
    
  } catch (error) {
    console.error('Error setting invoice counter value:', error);
    return {
      success: false,
      message: `Error setting counter: ${error.message}`
    };
  }
};

/**
 * Get invoice counter information and validation
 * @param {number} year - Year to check
 * @returns {Promise<Object>} Detailed counter information
 */
export const getInvoiceCounterInfo = async (year) => {
  try {
    const counterInfo = await getInvoiceCounterValue(year);
    const highestExisting = await getHighestInvoiceNumber(year);
    
    // Check for potential issues
    const issues = [];
    if (counterInfo.count <= highestExisting) {
      issues.push(`Counter (${counterInfo.count}) is not higher than highest existing invoice (${highestExisting})`);
    }
    
    return {
      counter: counterInfo,
      highestExisting: highestExisting,
      nextNumber: Math.max(counterInfo.count + 1, highestExisting + 1),
      issues: issues,
      isHealthy: issues.length === 0
    };
  } catch (error) {
    console.error('Error getting invoice counter info:', error);
    return null;
  }
};

/**
 * Reset invoice counter to safe value (highest existing + 1)
 * @param {number} year - Year to reset counter for
 * @returns {Promise<Object>} Result with success status and message
 */
export const resetInvoiceCounterToSafe = async (year) => {
  try {
    const highestExisting = await getHighestInvoiceNumber(year);
    const safeValue = highestExisting + 1;
    
    const result = await setInvoiceCounterValue(year, safeValue, 'Auto-reset to safe value');
    
    if (result.success) {
      return {
        success: true,
        message: `Counter reset to safe value: ${safeValue}`,
        newValue: safeValue,
        previousHighest: highestExisting
      };
    }
    
    return result;
  } catch (error) {
    console.error('Error resetting invoice counter:', error);
    return {
      success: false,
      message: `Error resetting counter: ${error.message}`
    };
  }
};

/**
 * Update invoice accounting submission status with full audit trail
 * @param {string} invoiceId - Invoice document ID
 * @param {boolean} submitted - New submission status
 * @param {Object} options - Additional options (reason, pdfVersion, pdfUrl)
 * @returns {Promise<Object>} Result with success status and message
 */
export const updateInvoiceAccountingStatus = async (invoiceId, submitted, options = {}) => {
  try {
    const { auth } = await import('./firebaseConfig');
    const currentUser = auth.currentUser?.email || 'system';
    const now = serverTimestamp();

    const invoiceRef = doc(db, INVOICES_COLLECTION, invoiceId);
    const invoiceSnap = await getDoc(invoiceRef);

    if (!invoiceSnap.exists()) {
      return {
        success: false,
        message: 'Invoice not found'
      };
    }

    const currentData = invoiceSnap.data();
    const currentHistory = currentData.accountingHistory || [];

    const updates = {
      submitToAccounting: submitted,
      updatedAt: now
    };

    // Create audit trail entry - only include defined values
    const historyEntry = {
      timestamp: new Date(),
      userId: currentUser,
      previousState: currentData.submitToAccounting || false,
      newState: submitted
    };

    if (submitted) {
      // Submitting to accounting
      updates.accountingSubmittedAt = now;
      updates.accountingSubmittedBy = currentUser;
      
      // Only add these fields if they have valid values (not undefined)
      if (options.pdfVersion !== undefined && options.pdfVersion !== null) {
        updates.accountingPdfVersion = options.pdfVersion;
        historyEntry.pdfVersion = options.pdfVersion;
      }
      if (options.pdfUrl !== undefined && options.pdfUrl !== null && options.pdfUrl !== '') {
        updates.accountingPdfUrl = options.pdfUrl;
      }

      historyEntry.action = 'submitted';
      historyEntry.reason = options.reason || 'Invoice finalized for accounting';
    } else {
      // Removing from accounting
      historyEntry.action = 'reverted';
      historyEntry.reason = options.reason || 'Removed from accounting';
      
      // Keep historical data but mark as reverted
      updates.accountingRevertedAt = now;
      updates.accountingRevertedBy = currentUser;
    }

    // Add to history
    updates.accountingHistory = [...currentHistory, historyEntry];

    await updateDoc(invoiceRef, updates);

    console.log(`✅ Invoice ${invoiceId} accounting status updated to: ${submitted}`);
    return {
      success: true,
      message: submitted ? 'Invoice submitted to accounting' : 'Invoice removed from accounting',
      historyEntry
    };
  } catch (error) {
    console.error('Error updating invoice accounting status:', error);
    return {
      success: false,
      message: `Error: ${error.message}`
    };
  }
};

/**
 * Record accounting edit event
 * @param {string} invoiceId - Invoice document ID
 * @param {string} reason - Reason for editing
 * @returns {Promise<Object>} Result with success status
 */
export const recordAccountingEdit = async (invoiceId, reason) => {
  try {
    const { auth } = await import('./firebaseConfig');
    const currentUser = auth.currentUser?.email || 'system';

    const invoiceRef = doc(db, INVOICES_COLLECTION, invoiceId);
    const invoiceSnap = await getDoc(invoiceRef);

    if (!invoiceSnap.exists()) {
      return { success: false, message: 'Invoice not found' };
    }

    const currentData = invoiceSnap.data();
    const currentHistory = currentData.accountingHistory || [];

    const historyEntry = {
      action: 'edited_while_submitted',
      timestamp: new Date(),
      userId: currentUser,
      reason: reason,
      previouslySubmittedAt: currentData.accountingSubmittedAt?.toDate(),
      previouslySubmittedBy: currentData.accountingSubmittedBy
    };

    const updates = {
      submitToAccounting: false, // Auto-revert to not submitted
      accountingRevertedAt: serverTimestamp(),
      accountingRevertedBy: currentUser,
      accountingHistory: [...currentHistory, historyEntry],
      updatedAt: serverTimestamp(),
      requiresAccountingResubmission: true // Flag for UI
    };

    await updateDoc(invoiceRef, updates);

    console.log(`✅ Accounting edit recorded for invoice ${invoiceId}`);
    return {
      success: true,
      message: 'Edit recorded. Invoice status reverted to "Not Submitted"',
      historyEntry
    };
  } catch (error) {
    console.error('Error recording accounting edit:', error);
    return {
      success: false,
      message: `Error: ${error.message}`
    };
  }
};

/**
 * Generate and store accounting PDF using Cloud Function (server-side upload)
 * @param {string} invoiceId - Invoice document ID
 * @param {Blob} pdfBlob - Generated PDF blob
 * @param {string} invoiceNumber - Invoice number
 * @returns {Promise<Object>} Result with PDF metadata
 */
export const storeAccountingPDF = async (invoiceId, pdfBlob, invoiceNumber) => {
  try {
    console.log(`📤 Starting Cloud Function PDF upload for invoice ${invoiceId}`);
    
    // Convert blob to base64
    const base64Data = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        // Get the base64 string (remove data URL prefix)
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(pdfBlob);
    });

    console.log(`✅ PDF converted to base64, size: ${base64Data.length} characters`);

    // Import Firebase functions
    const { getFunctions, httpsCallable } = await import('firebase/functions');
    const functions = getFunctions();
    
    // Call Cloud Function
    const uploadPDF = httpsCallable(functions, 'uploadAccountingPDF');
    
    console.log('🚀 Calling uploadAccountingPDF Cloud Function...');
    const result = await uploadPDF({
      pdfData: base64Data,
      documentType: 'invoice',
      documentId: invoiceId,
      documentNumber: invoiceNumber
    });

    console.log('✅ Cloud Function response:', result.data);

    if (result.data.success) {
      return {
        success: true,
        metadata: result.data.metadata,
        version: result.data.version,
        url: result.data.url,
        message: result.data.message
      };
    } else {
      throw new Error(result.data.message || 'Upload failed');
    }
  } catch (error) {
    console.error('❌ Error storing accounting PDF:', error);
    
    // Extract meaningful error message
    let errorMessage = 'Failed to store PDF';
    if (error.code === 'unauthenticated') {
      errorMessage = 'You must be logged in to upload PDFs';
    } else if (error.code === 'permission-denied') {
      errorMessage = 'You do not have permission to upload PDFs';
    } else if (error.message) {
      errorMessage = error.message;
    }
    
    return {
      success: false,
      message: errorMessage
    };
  }
};

/**
 * Check if invoice can be edited (accounting workflow validation)
 * @param {string} invoiceId - Invoice document ID
 * @returns {Promise<Object>} Validation result
 */
export const validateInvoiceEditPermission = async (invoiceId) => {
  try {
    const invoiceRef = doc(db, INVOICES_COLLECTION, invoiceId);
    const invoiceSnap = await getDoc(invoiceRef);

    if (!invoiceSnap.exists()) {
      return {
        canEdit: false,
        requiresConfirmation: false,
        message: 'Invoice not found'
      };
    }

    const data = invoiceSnap.data();

    // Check if submitted to accounting
    if (data.submitToAccounting) {
      return {
        canEdit: true,
        requiresConfirmation: true,
        isSubmittedToAccounting: true,
        submittedAt: data.accountingSubmittedAt?.toDate(),
        submittedBy: data.accountingSubmittedBy,
        message: 'This invoice has been submitted to accounting. Editing will require re-submission.'
      };
    }

    // Check if it's a paid invoice
    if (data.status === INVOICE_STATUSES.PAID) {
      return {
        canEdit: true,
        requiresConfirmation: true,
        isPaidInvoice: true,
        message: 'This invoice is marked as paid. Are you sure you want to edit it?'
      };
    }

    return {
      canEdit: true,
      requiresConfirmation: false,
      message: 'Invoice can be edited'
    };
  } catch (error) {
    console.error('Error validating invoice edit permission:', error);
    return {
      canEdit: false,
      requiresConfirmation: false,
      message: `Error: ${error.message}`
    };
  }
};

/**
 * Create invoice from CSV data without generating new number
 * @param {Object} invoiceData - Invoice data from CSV
 * @param {string} preservedNumber - Original invoice number from CSV
 * @returns {Promise<Object>} Created invoice with ID
 */
export const createInvoiceFromCSV = async (invoiceData, preservedNumber) => {
  try {
    // Validate preservedNumber format (BSIN-MMYYYY-XXX)
    const numberPattern = /^BSIN-\d{6}-\d{3}$/;
    if (!numberPattern.test(preservedNumber)) {
      throw new Error(`Invalid invoice number format: ${preservedNumber}. Expected format: BSIN-MMYYYY-XXX`);
    }

    // Check if number already exists
    const exists = await checkInvoiceNumberExists(preservedNumber);
    if (exists) {
      throw new Error(`Invoice number ${preservedNumber} already exists`);
    }

    // Create invoice document with preserved number
    const invoice = {
      ...invoiceData,
      invoiceNumber: preservedNumber,
      status: invoiceData.status || 'draft',
      createdAt: invoiceData.invoiceDate ? Timestamp.fromDate(new Date(invoiceData.invoiceDate)) : serverTimestamp(),
      updatedAt: serverTimestamp(),
      version: 1,
      isHistoricalImport: true,
      importMetadata: {
        importedAt: serverTimestamp(),
        importedBy: invoiceData.importedBy || 'system'
      }
    };

    const docRef = await addDoc(collection(db, INVOICES_COLLECTION), invoice);

    // Trigger schedule sync if the invoice has dates
    const hasDates = invoice.dates && Array.isArray(invoice.dates) && invoice.dates.length > 0;
    if (hasDates) {
      try {
        const { syncSchedulesFromInvoices } = await import('./scheduleService');
        await syncSchedulesFromInvoices();
        console.log('✅ Schedule sync completed for CSV imported invoice');
      } catch (error) {
        console.warn('⚠️ Failed to sync schedules for CSV imported invoice:', error);
        // Don't fail the import if schedule sync fails
      }
    }

    return {
      id: docRef.id,
      ...invoice,
      invoiceNumber: preservedNumber
    };
  } catch (error) {
    console.error('Error creating invoice from CSV:', error);
    throw error;
  }
};

/**
 * Bulk create multiple invoices from CSV import
 * @param {Array<Object>} invoicesArray - Array of invoice data with preserved numbers
 * @returns {Promise<Object>} Import results summary
 */
export const bulkCreateInvoices = async (invoicesArray) => {
  const results = {
    total: invoicesArray.length,
    successful: 0,
    failed: 0,
    errors: [],
    importedDocuments: []
  };

  // Process in batches of 50
  const batchSize = 50;
  for (let i = 0; i < invoicesArray.length; i += batchSize) {
    const batch = invoicesArray.slice(i, i + batchSize);

    for (const invoiceData of batch) {
      try {
        const created = await createInvoiceFromCSV(
          invoiceData.data,
          invoiceData.invoiceNumber
        );
        results.successful++;
        results.importedDocuments.push(created.id);
      } catch (error) {
        results.failed++;
        results.errors.push({
          line: invoiceData.lineNumber || i,
          invoiceNumber: invoiceData.invoiceNumber,
          error: error.message
        });
        console.error(`Failed to import invoice ${invoiceData.invoiceNumber}:`, error);
      }
    }

    // Small delay between batches to avoid rate limiting
    if (i + batchSize < invoicesArray.length) {
      await new Promise(resolve => setTimeout(resolve, 500));
    }
  }

  console.log('Bulk invoice import completed:', results);
  return results;
};
